import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { cn } from '../../../utils/cn';

const ResourceCard = ({ resource }) => {
  const [isBookmarked, setIsBookmarked] = useState(resource?.bookmarked || false);
  const [showPreview, setShowPreview] = useState(false);

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'beginner':
        return 'text-success bg-success/10';
      case 'intermediate':
        return 'text-warning bg-warning/10';
      case 'advanced':
        return 'text-error bg-error/10';
      default:
        return 'text-muted-foreground bg-muted';
    }
  };

  const getCompletionColor = (status) => {
    switch (status) {
      case 'completed':
        return 'text-success bg-success/10';
      case 'in-progress':
        return 'text-warning bg-warning/10';
      case 'not-started':
        return 'text-muted-foreground bg-muted';
      default:
        return 'text-muted-foreground bg-muted';
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'reading-guide':
        return 'BookOpen';
      case 'knowledge-area':
        return 'Brain';
      case 'article':
        return 'FileText';
      case 'downloadable':
        return 'Download';
      case 'interactive':
        return 'MousePointerClick';
      case 'checklist':
        return 'CheckSquare';
      default:
        return 'File';
    }
  };

  const handleDownload = () => {
    console.log('Downloading:', resource?.title);
  };

  const toggleBookmark = () => {
    setIsBookmarked(!isBookmarked);
  };

  return (
    <div className="bg-card rounded-lg shadow-elevation-1 hover:shadow-elevation-2 transition-shadow overflow-hidden">
      {/* Card Header */}
      <div className="p-4 border-b border-border">
        <div className="flex items-start justify-between mb-2">
          <div className="flex items-center gap-2">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Icon name={getTypeIcon(resource?.type)} size={20} className="text-primary" />
            </div>
            <div className="flex flex-col">
              <span className={cn(
                'text-xs font-medium px-2 py-1 rounded-full',
                getDifficultyColor(resource?.difficulty)
              )}>
                {resource?.difficulty?.charAt(0)?.toUpperCase() + resource?.difficulty?.slice(1)}
              </span>
            </div>
          </div>
          <button
            onClick={toggleBookmark}
            className="p-2 hover:bg-muted rounded-lg transition-colors"
            aria-label={isBookmarked ? 'Remove bookmark' : 'Add bookmark'}
          >
            <Icon 
              name={isBookmarked ? 'Bookmark' : 'BookmarkPlus'} 
              size={20} 
              className={isBookmarked ? 'text-accent fill-accent' : 'text-muted-foreground'}
            />
          </button>
        </div>
        <h3 className="text-base font-semibold text-foreground mb-2 line-clamp-2">
          {resource?.title}
        </h3>
        <p className="text-sm text-muted-foreground line-clamp-2">
          {resource?.description}
        </p>
      </div>

      {/* Card Body */}
      <div className="p-4 space-y-3">
        {/* Knowledge Area */}
        <div className="flex items-center gap-2 text-sm">
          <Icon name="Target" size={16} className="text-muted-foreground" />
          <span className="text-muted-foreground line-clamp-1">{resource?.knowledgeArea}</span>
        </div>

        {/* Reading Time & Rating */}
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center gap-2">
            <Icon name="Clock" size={16} className="text-muted-foreground" />
            <span className="text-muted-foreground">{resource?.readingTime} min</span>
          </div>
          <div className="flex items-center gap-1">
            <Icon name="Star" size={16} className="text-accent fill-accent" />
            <span className="text-foreground font-medium">{resource?.rating}</span>
            <span className="text-muted-foreground">({resource?.ratingCount})</span>
          </div>
        </div>

        {/* Format & File Size */}
        {resource?.downloadable && (
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center gap-2">
              <Icon name="FileType" size={16} className="text-muted-foreground" />
              <span className="text-muted-foreground">{resource?.format}</span>
            </div>
            {resource?.fileSize && (
              <span className="text-muted-foreground">{resource?.fileSize}</span>
            )}
          </div>
        )}

        {/* Completion Status */}
        <div className="flex items-center gap-2">
          <span className={cn(
            'text-xs font-medium px-3 py-1 rounded-full',
            getCompletionColor(resource?.completionStatus)
          )}>
            {resource?.completionStatus === 'not-started' && 'Not Started'}
            {resource?.completionStatus === 'in-progress' && 'In Progress'}
            {resource?.completionStatus === 'completed' && 'Completed'}
          </span>
        </div>
      </div>

      {/* Card Footer */}
      <div className="p-4 bg-muted/30 border-t border-border flex gap-2">
        <Button
          variant="outline"
          size="sm"
          iconName="Eye"
          onClick={() => setShowPreview(!showPreview)}
          className="flex-1"
        >
          Preview
        </Button>
        {resource?.downloadable ? (
          <Button
            variant="default"
            size="sm"
            iconName="Download"
            onClick={handleDownload}
            className="flex-1"
          >
            Download
          </Button>
        ) : (
          <Button
            variant="default"
            size="sm"
            iconName="ExternalLink"
            onClick={() => window?.open('#', '_blank')}
            className="flex-1"
          >
            View
          </Button>
        )}
      </div>

      {/* Preview Modal Overlay */}
      {showPreview && (
        <div 
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={() => setShowPreview(false)}
        >
          <div 
            className="bg-card rounded-lg max-w-2xl w-full max-h-[80vh] overflow-y-auto p-6"
            onClick={(e) => e?.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-4">
              <h3 className="text-xl font-semibold text-foreground">{resource?.title}</h3>
              <button
                onClick={() => setShowPreview(false)}
                className="p-2 hover:bg-muted rounded-lg transition-colors"
              >
                <Icon name="X" size={20} />
              </button>
            </div>
            <div className="space-y-4">
              <p className="text-foreground">{resource?.description}</p>
              <div className="bg-muted/50 p-4 rounded-lg">
                <h4 className="font-semibold text-foreground mb-2">Resource Details</h4>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li><strong>Knowledge Area:</strong> {resource?.knowledgeArea}</li>
                  <li><strong>Difficulty:</strong> {resource?.difficulty}</li>
                  <li><strong>Reading Time:</strong> {resource?.readingTime} minutes</li>
                  <li><strong>Format:</strong> {resource?.format}</li>
                  {resource?.fileSize && <li><strong>File Size:</strong> {resource?.fileSize}</li>}
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ResourceCard;